/**
 * 기사앱 — 담당자 웹과 완전히 분리된 트리. `/driver/*` 아래에서만 산다.
 *
 * UI 원칙(기사 대부분 60세 이상, docuservice 계승):
 *  본문 17px 하한 · 터치 타겟 56px(min-h-touch) · **한 화면에 한 일** · 주관식 최소 · 개발 용어 금지.
 */

import { Navigate, Route, Routes } from 'react-router-dom';
import { DriverAuthProvider, useDriverAuth } from '../lib/driverAuth';
import Login from './Login';
import Home from './Home';
import Bids from './Bids';
import MyBids from './MyBids';
import MyDispts from './MyDispts';
import Notices from './Notices';
import NoticeDetail from './NoticeDetail';
import Salary from './Salary';
import SalaryDetail from './SalaryDetail';
import Docs from './Docs';
import MyInvoices from './MyInvoices';
import NewInvoice from './NewInvoice';
import MyOrders from './MyOrders';
import NewOrder from './NewOrder';
import ReserveSplit from './ReserveSplit';
import AppPrompts from './AppPrompts';

export default function DriverApp() {
  return (
    <DriverAuthProvider>
      <div className="driver-shell">
        <div className="driver-frame">
          <Gate />
        </div>
      </div>
    </DriverAuthProvider>
  );
}

/**
 * 관리자 「기사앱으로 보기」 띠 — 화면 아래 고정(위쪽은 기사앱 머리·앱 바가 쓴다).
 * 설치·알림 안내는 띄우지 않는다(관리자 브라우저를 그 기사 폰으로 올리지 않게 — 서버도 등록을 건너뛴다).
 */
function ViewAsBar({ until }: { until: number }) {
  const { driver, logout } = useDriverAuth();
  const end = new Date(until).toTimeString().slice(0, 5);
  const close = async () => {
    await logout().catch(() => {});
    window.close();
  };
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-center gap-3 bg-amber-500 px-4 py-2 text-sm font-semibold text-white shadow-lg"
      style={{ paddingBottom: 'calc(0.5rem + var(--sab, 0px))' }}
    >
      <span>관리자 보기 · {driver?.driver_name ?? '기사'} · 읽기 전용(저장 안 됨) · {end}까지</span>
      <button type="button" onClick={close} className="rounded bg-white/90 px-2 py-0.5 text-amber-700">종료</button>
    </div>
  );
}

function Gate() {
  const { user, loading, acting, viewAs } = useDriverAuth();

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center text-lg text-ink-soft sm:min-h-[calc(100vh-3rem)]">
        <span className="flex items-center gap-3">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-brand-100 border-t-brand-600" aria-hidden />
          불러오는 중…
        </span>
      </div>
    );
  }
  if (!user) {
    return (
      <>
        <Login />
        <AppPrompts loggedIn={false} />
      </>
    );
  }

  return (
    <>
      {viewAs ? <ViewAsBar until={viewAs.until} /> : <AppPrompts loggedIn />}
      {/* 차주가 「누구 것으로」를 바꾸면(#258) 화면을 새로 띄운다 — 받아 둔 목록·입력이 앞 사람 것으로 남지 않게. */}
      <Routes key={acting ?? 'self'}>
        <Route path="/" element={<Home />} />
        <Route path="/bids" element={<Bids />} />
        <Route path="/my-bids" element={<MyBids />} />
        <Route path="/my-dispts" element={<MyDispts />} />
        <Route path="/notices" element={<Notices />} />
        <Route path="/notices/:id" element={<NoticeDetail />} />
        <Route path="/salary" element={<Salary />} />
        <Route path="/salary/:mon" element={<SalaryDetail />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/car-docs" element={<Docs mode="car" />} />
        <Route path="/invoices" element={<MyInvoices />} />
        <Route path="/invoices/new" element={<NewInvoice />} />
        <Route path="/orders" element={<MyOrders />} />
        <Route path="/orders/new" element={<NewOrder />} />
        <Route path="/dasu" element={<MyOrders dasu />} />
        <Route path="/reserve-split" element={<ReserveSplit />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
